import React, { cloneElement, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Popover,
  ListItem as MuiListItem,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  useTheme,
  useMediaQuery,
  Box,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import { ChevronLeft as ChevronLeftIcon } from '@mui/icons-material';
import ProtectedComponent from '../../../components/main/protected/ProtectedComponent';
import withRouter from '../../../components/hocs/withRouter';
import { PubSub } from '../../../utils/eventUtils';
import { RailTooltip } from './RailTooltip';

export const SidebarSecondaryNav = withRouter(
  ({
    routes = [],
    children,
    location,
    parent,
    railCollapsed = false,
    ...props
  }: any) => {
    const popoverClasses = usePopoverClasses();
    const itemClasses = useItemStyles();
    const itemTextClasses = useItemTextStyles();
    const closeButtonClasses = useCloseButton();
    const headerClasses = useHeaderStyles();
    const itemIconClasses = useIconStyles();
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('md'));

    const [open, setopen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = useCallback(
      (event: any) => {
        if (matches && open) {
          // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
          PubSub.publish('mobile-menu:close');
        }

        setAnchorEl(event.target.closest('[data-submenu]'));
        setopen((current) => !current);
      },
      [matches, open]
    );

    return (
      <>
        {/* A flyout opens on click, so in the rail hovering its parent row
          would otherwise say nothing about what it holds. */}
        <RailTooltip railCollapsed={railCollapsed} title={parent}>
          {cloneElement(children, { onClick: handleClick })}
        </RailTooltip>
        <Popover
          anchorEl={anchorEl}
          open={open}
          onClose={handleClick}
          classes={popoverClasses}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          {...props}
        >
          <header className={headerClasses.root}>{parent}</header>
          {routes
            .filter((r: any) => r.nav)
            .map(({ nav, ...route }: any) => {
              // Strip only a trailing OPTIONAL route param segment (e.g. the
              // `/:tab?` in a tab subrouter's path) before using it as a Link
              // target — a raw template string would otherwise navigate to
              // the literal ":tab?" segment. Required params (e.g.
              // `/:accountId`) are left in place: routes like Org. Profile
              // rely on their own resolver component to replace the literal
              // placeholder with a real id (see OrgProfileResolver).
              const to = route.path.replace(/\/:[^/]+\?$/, '');

              return (
                <ProtectedComponent
                  allow={route.allow}
                  allowEither={route.allowEither}
                  allowIfAll={route.allowIfAll}
                  key={route.path}
                >
                  <MuiListItem
                    button
                    classes={itemClasses}
                    component={Link}
                    selected={location.pathname.startsWith(to)}
                    to={to}
                    onClick={handleClick}
                    data-qa={`SIDEBAR_SUBNAVIGATION:${nav.name}`}
                  >
                    {nav.icon && (
                      <MuiListItemIcon classes={itemIconClasses}>
                        {nav.icon}
                      </MuiListItemIcon>
                    )}
                    <MuiListItemText
                      classes={itemTextClasses}
                      disableTypography
                      secondary={nav.name}
                    />
                  </MuiListItem>
                </ProtectedComponent>
              );
            })}
          <Box className={closeButtonClasses.root} onClick={handleClick}>
            <ChevronLeftIcon />
          </Box>
        </Popover>
      </>
    );
  }
);

const usePopoverClasses = makeStyles(({ palette }) => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 150,
    paddingBottom: '0.9375rem',
    marginLeft: -4,
    positon: 'relative',
    background: palette.mode === 'dark' ? '#444353' : '#ebebeb',
    borderRadius: 0,
  },
}));

const useItemStyles = makeStyles(({ palette }) => ({
  root: {
    padding: '0.9375rem 2.125rem 0.9375rem 1.25rem',
    position: 'relative',
    textTransform: 'uppercase',

    '&:after': {
      content: '""',
      height: 2,
      position: 'absolute',
      bottom: 0,
      left: '0.9375rem',
      right: '2.125rem',
      background: palette.background.default,
    },

    '&:hover > div': {
      color: palette.primary.contrastText,
    },

    '&:hover:after': {
      background: palette.common.cowbellBlue,
    },
  },

  selected: {
    color: palette.text.link,
    backgroundColor: 'transparent !important',

    '&:after': {
      background: palette.common.cowbellBlue,
    },
  },
}));

const useItemTextStyles = makeStyles(({ config }) => ({
  root: {
    margin: 0,
    fontSize: config.textSizes.normal,
  },
}));

const useHeaderStyles = makeStyles(({ palette }) => ({
  root: {
    padding: '0.9375rem 2.125rem 0.9375rem 1.25rem',
    background: palette.mode === 'dark' ? '#31303E' : '#CAD3DE',
    color: palette.mode === 'dark' ? palette.primary.contrastText : '#27242C',
    fontWeight: 600,
  },
}));

const useCloseButton = makeStyles(({ palette, shape }) => ({
  root: {
    paddingTop: 8,
    paddingBottom: 3,
    position: 'absolute',
    top: '50%',
    right: 0,
    transform: 'translateY(-50%)',
    background: palette.background.default,
    borderTopLeftRadius: shape.borderRadius,
    borderBottomLeftRadius: shape.borderRadius,
    color: palette.common.cowbellBlue,

    '&:hover': {
      color: palette.common.cowbellBlue,
    },
  },
}));
const useIconStyles = makeStyles(() => ({
  root: {
    '& svg': {
      minWidth: 'unset',
      maxWidth: '100%',
      width: '2.0833333333rem',
      height: '2.0833333333rem',
    },

    '.small & svg': {
      width: '1.6666666667rem',
      height: '1.6666666667rem',
    },
  },
}));
